/**
 * Browser tab titles, driven by the `title` on each route.
 *
 * Without this every page is called "Dormotech": the tab is useless once a user
 * has three of them open, and analytics reports collapse every page into a
 * single "Dormotech" row, since GA reads the page title from the document.
 *
 * A route with no `title` of its own inherits its nearest titled ancestor, so
 * top-level routes give every page a sensible name and children only need a
 * title when they deserve a more specific one.
 */
import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';

/** Suffix on every tab title, so a Dormotech tab is recognizable among others. */
export const APP_TITLE = 'Dormotech';

/** Separator between the page name and the app name. */
const SEPARATOR = ' · ';

/**
 * Deepest `title` in an activated route chain, or undefined when no route in it
 * declares one.
 *
 * Exported for callers that need the title of the page being navigated to
 * *without* reading `document.title` — analytics is the case that matters, since
 * whether the document has been updated yet depends on router internals.
 */
export function deepestRouteTitle(
  snapshot: RouterStateSnapshot,
): string | undefined {
  let route = snapshot.root;
  let title: string | undefined;

  while (route) {
    title = route.title ?? title;
    if (!route.firstChild) break;
    route = route.firstChild;
  }

  return title;
}

@Injectable({ providedIn: 'root' })
export class PageTitleStrategy extends TitleStrategy {
  constructor(private readonly title: Title) {
    super();
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const pageTitle = this.buildTitle(snapshot);
    this.title.setTitle(
      pageTitle ? `${pageTitle}${SEPARATOR}${APP_TITLE}` : APP_TITLE,
    );
  }
}
